import moment from 'moment';
import { SurveyAnswers } from '../../types/TimelineItemTypes';

export const moveResizeValidator = (time: number): number => {
  if (time < new Date().getTime()) {
    const newTime =
      Math.ceil(new Date().getTime() / (15 * 60 * 1000)) * (15 * 60 * 1000);
    return newTime;
  }

  return time;
};

/**
 * generate vertical line class names for time range
 * @param timeStart - start time timestamp
 * @param timeEnd - end time timestamp
 * @returns CSS class names array
 */
export const verticalLineClassNamesForTime = (
  timeStart: number,
  timeEnd: number,
): string[] => {
  const currentTimeStart = moment(timeStart);
  const currentTimeEnd = moment(timeEnd);

  const classes: string[] = [];

  const monthStart = moment().startOf('month');
  const monthEnd = moment().startOf('month').add(1, 'month');
  if (currentTimeStart >= monthStart && currentTimeEnd <= monthEnd) {
    classes.push('currentMonthMarker');
  }

  return classes;
};

/** Extract displayable fields from FHIR resource
 * @param fhir - FHIR resource object
 * @returns Array of objects with title and value for display
 */

export const extractFHIRDisplay = (
  fhir: Record<string, any>,
): { title: string; value: any }[] => {
  const fhirDisplays: { title: string; value: any }[] = [];
  if (!fhir) return fhirDisplays;

  fhirDisplays.push(...aishSleepDisplay(fhir));

  for (const [key, value] of Object.entries(fhir)) {
    if (key == 'dosageInstruction' && value.text) {
      fhirDisplays.push({ title: 'Dosage instruction', value: value.text });
    }
  }
  return fhirDisplays;
};

export const extractSurveyDisplay = (
  survey: SurveyAnswers[],
): { title: string; value: any }[] => {
  const surveyDisplays: { title: string; value: any }[] = [];
  if (!survey || survey.length === 0) return surveyDisplays;

  for (const answer of survey) {
    surveyDisplays.push({ title: answer.question, value: answer.answer });
  }
  return surveyDisplays;
};

/**
 * Displays fields needing displaying from AISH Sleep FHIR resource
 * @param fhir - FHIR resource object
 * @returns Array of objects with title and value for display
 */
const aishSleepDisplay = (
  fhir: Record<string, any>,
): { title: string; value: any }[] => {
  const fhirDisplays: { title: string; value: any }[] = [];

  // Special handling for Withings sleep summaries
  const isWithingsSleep = Boolean(
    fhir.code?.coding?.some(
      (c: any) =>
        c.code === 'sleep-summary' &&
        c.system === 'https://caremappr.com/withings',
    ),
  );

  if (isWithingsSleep) {
    const start = fhir.effectivePeriod?.start;
    const end = fhir.effectivePeriod?.end;
    if (start)
      fhirDisplays.push({
        title: 'Sleep start time',
        value: moment(start).format('HH:mm (DD MMM)'),
      });
    if (end)
      fhirDisplays.push({
        title: 'Sleep end time',
        value: moment(end).format('HH:mm (DD MMM)'),
      });

    const components: any[] = fhir.component || [];

    const sleepComp = components.find((c) =>
      c.code?.coding?.some((cd: any) => cd.code === 'sleep_duration'),
    );
    let durationDisplay = 'Not available';
    if (sleepComp) {
      const raw =
        sleepComp?.valueQuantity?.value ??
        sleepComp?.valueInteger ??
        sleepComp?.valueString ??
        null;
      const unit = (sleepComp?.valueQuantity?.unit ?? '').toLowerCase();
      if (raw === null || raw === '' || raw === undefined) {
        durationDisplay = 'Not available';
      } else {
        let totalSeconds: number | null = null;
        if (unit.includes('s')) totalSeconds = Number(raw);
        else if (unit.includes('min')) totalSeconds = Number(raw) * 60;
        else if (unit.includes('h')) totalSeconds = Number(raw) * 3600;
        else totalSeconds = Number(raw);

        if (!isNaN(totalSeconds as number)) {
          const hrs = Math.floor((totalSeconds as number) / 3600);
          const mins = Math.floor(((totalSeconds as number) % 3600) / 60);
          durationDisplay = `${hrs}h ${mins}m`;
        } else {
          durationDisplay = String(raw);
        }
      }
    }
    fhirDisplays.push({ title: 'Sleep duration', value: durationDisplay });

    const ahiComp = components.find((c) =>
      c.code?.coding?.some((cd: any) => cd.code === 'apnea_hypopnea_index'),
    );
    const ahiRaw =
      ahiComp?.valueQuantity?.value ??
      ahiComp?.valueInteger ??
      ahiComp?.valueString ??
      null;
    const ahiUnit = ahiComp?.valueQuantity?.unit ?? 'events/hr';
    const ahiDisplay =
      ahiRaw === null ? 'Not available' : `${ahiRaw} ${ahiUnit}`;
    fhirDisplays.push({ title: 'AHI', value: ahiDisplay });

    const snoreComp = components.find((c) =>
      c.code?.coding?.some((cd: any) => cd.code === 'snoring'),
    );
    const snoreRaw =
      snoreComp?.valueString ??
      snoreComp?.valueBoolean ??
      snoreComp?.valueQuantity?.value ??
      null;
    const snoreUnit = snoreComp?.valueQuantity?.unit ?? null;
    let snoreDisplay: any;
    if (snoreRaw === null || snoreRaw === '' || snoreRaw === undefined) {
      snoreDisplay = 'Not available';
    } else if (typeof snoreRaw === 'boolean') {
      snoreDisplay = snoreRaw ? 'Yes' : 'No';
    } else if (snoreUnit) {
      snoreDisplay = `${snoreRaw} ${snoreUnit}`;
    } else {
      snoreDisplay = snoreRaw;
    }
    fhirDisplays.push({ title: 'Snoring', value: snoreDisplay });
  }
  return fhirDisplays;
};
